import { initComponent } from './core/init-component.js'

const DEFAULT_MOBILE_MEDIA_QUERY = '(max-width: 767px)'
const DEFAULT_WISTIA_OPTIONS = {
  videoFoam: true,
  fitStrategy: 'cover',
  endVideoBehavior: 'loop',
  playSuspendedOffScreen: false,
  playPauseNotifier: false,
}

const parseBoolean = value => value === '' || value === 'true' || value === '1'

const parseJson = value => {
  if (!value) {
    return {}
  }

  try {
    return JSON.parse(value)
  } catch (error) {
    console.warn('Invalid data-wistia-options JSON', error)
    return {}
  }
}

const getActiveVideoId = ({ desktopVideoId, mobileVideoId, onlyMobile, onlyDesktop, isMobile }) => {
  if (onlyMobile) {
    return mobileVideoId || desktopVideoId
  }

  if (onlyDesktop) {
    return desktopVideoId || mobileVideoId
  }

  if (isMobile && mobileVideoId) {
    return mobileVideoId
  }

  return desktopVideoId || mobileVideoId
}

initComponent('.js-adaptive-wistia-video', snippet => {
  const desktopVideoId = snippet.dataset.desktopVideoId?.trim()
  const mobileVideoId = snippet.dataset.mobileVideoId?.trim()
  const mobileMediaQuery = snippet.dataset.mobileMediaQuery || DEFAULT_MOBILE_MEDIA_QUERY
  const onlyMobile = parseBoolean(snippet.dataset.onlyMobile)
  const onlyDesktop = parseBoolean(snippet.dataset.onlyDesktop)
  const wistiaControls = parseBoolean(snippet.dataset.wistiaControls)
  const autoplay = parseBoolean(snippet.dataset.autoplay)
  const muted = parseBoolean(snippet.dataset.muted)
  const cssScope = snippet.dataset.cssScope || 'wistia-embed'
  const wistiaOptions = parseJson(snippet.dataset.wistiaOptions)

  if (!desktopVideoId && !mobileVideoId) {
    return
  }

  window._wq = window._wq || []

  const mediaQueryList = window.matchMedia(mobileMediaQuery)
  let activeVideoId = ''
  let player = null
  let paused = true
  let isHovered = false

  const toggle = snippet.querySelector('.js-adaptive-wistia-video-toggle')
  const playIcon = toggle?.querySelector('.js-icon-play')
  const pauseIcon = toggle?.querySelector('.js-icon-pause')

  const videoContainer = document.createElement('div')
  snippet.prepend(videoContainer)

  const updateToggle = () => {
    if (!toggle) return
    playIcon?.classList.toggle('hidden', !paused)
    pauseIcon?.classList.toggle('hidden', paused)
    toggle.setAttribute('aria-label', paused ? 'Play video' : 'Pause video')
  }

  if (toggle) {
    toggle.addEventListener('click', e => {
      e.stopPropagation()
      e.preventDefault()
      if (paused) {
        player?.play()
      } else {
        player?.pause()
      }
    })
  }

  const buildOptions = () => ({
    ...DEFAULT_WISTIA_OPTIONS,
    controlsVisibleOnLoad: wistiaControls,
    autoPlay: autoplay,
    muted,
    ...wistiaOptions,
  })

  const renderEmbed = videoId => {
    if (!videoId || videoId === activeVideoId) {
      return
    }

    activeVideoId = videoId
    player?.pause?.()
    player?.remove?.()
    player = null
    paused = true
    updateToggle()

    videoContainer.innerHTML = `<div class="${cssScope}__video wistia_embed wistia_async_${videoId}"></div>`

    window._wq.push({
      id: videoId,
      options: buildOptions(),
      onReady: video => {
        player = video

        video.bind('play', () => {
          paused = false
          updateToggle()
        })
        video.bind('pause', () => {
          paused = true
          updateToggle()
        })
        video.bind('end', () => {
          paused = true
          updateToggle()
        })

        if (autoplay) {
          video.play()
        }
      },
    })
  }

  const syncVideo = () => {
    renderEmbed(
      getActiveVideoId({
        desktopVideoId,
        mobileVideoId,
        onlyMobile,
        onlyDesktop,
        isMobile: mediaQueryList.matches,
      })
    )
  }

  const onMediaChange = () => {
    // Reset activeVideoId so renderEmbed forces a re-render on breakpoint change
    activeVideoId = ''
    syncVideo()
  }

  if (mediaQueryList.addEventListener) {
    mediaQueryList.addEventListener('change', onMediaChange)
  } else {
    mediaQueryList.addEListener(onMediaChange)
  }

  syncVideo()

  return {
    unload() {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', onMediaChange)
      } else {
        mediaQueryList.removeEventListener(onMediaChange)
      }

      player?.pause?.()
      player?.remove?.()
      videoContainer.innerHTML = ''
    },
  }
})
